import React, { useCallback } from 'react';
import { Upload, Image, FileText, X, Globe, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { OnboardingFormData } from '../OnboardingWizardContent';

interface Props {
  data: OnboardingFormData;
  updateData: (updates: Partial<OnboardingFormData>) => void;
}

const StepBrandKit: React.FC<Props> = ({ data, updateData }) => {
  const handleLogoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      updateData({ logoFile: file, logoUrl: URL.createObjectURL(file) });
    }
  }, [updateData]);

  const handleBrandGuidelineUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      updateData({ brandGuidelineFile: file, brandGuidelineUrl: URL.createObjectURL(file) });
    }
  }, [updateData]);

  const removeLogo = () => {
    updateData({ logoFile: null, logoUrl: '' });
  };

  const removeBrandGuideline = () => {
    updateData({ brandGuidelineFile: null, brandGuidelineUrl: '' });
  };

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Upload your logo and brand guidelines to help us match your assistant's appearance 
        to your brand identity. <span className="text-sm">(Optional)</span>
      </p>

      {/* Use Website Design Checkbox */}
      <div className="flex items-start gap-3 p-4 border rounded-lg bg-muted/50">
        <Checkbox
          id="use-website-design"
          checked={data.useWebsiteAsDesignReference}
          onCheckedChange={(checked) =>
            updateData({ useWebsiteAsDesignReference: checked === true })
          }
          className="mt-1"
        />
        <div>
          <label htmlFor="use-website-design" className="font-medium cursor-pointer flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Use my website design as design reference
          </label>
          <p className="text-sm text-muted-foreground mt-1">
            We'll analyze your website to extract colors, fonts, and design style for your assistant. 
            You can still upload assets below to override specific elements.
          </p>
        </div>
      </div>

      {/* Info banner when checkbox is checked */}
      {data.useWebsiteAsDesignReference && (
        <div className="bg-brand-teal/10 border border-brand-teal/30 rounded-lg p-3 flex items-start gap-2">
          <Info className="h-4 w-4 text-brand-teal mt-0.5 shrink-0" />
          <p className="text-sm text-card-foreground">
            Your website design will be used as the primary reference. File uploads below are optional overrides.
          </p>
        </div>
      )}

      {/* Logo Upload */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Logo {data.useWebsiteAsDesignReference && <span className="text-muted-foreground font-normal">(optional override)</span>}
        </label>
        {data.logoUrl ? (
          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
            <img 
              src={data.logoUrl} 
              alt="Logo preview" 
              className="h-16 w-16 object-contain rounded"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{data.logoFile?.name}</p>
              <p className="text-xs text-muted-foreground">
                {data.logoFile && (data.logoFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={removeLogo}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Image className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Click to upload logo (PNG, JPG, SVG)
              </p>
            </div>
            <input 
              type="file" 
              className="hidden" 
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={handleLogoUpload}
            />
          </label>
        )}
      </div>

      {/* Brand Guideline Upload */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Brand Guidelines {data.useWebsiteAsDesignReference && <span className="text-muted-foreground font-normal">(optional override)</span>}
        </label>
        {data.brandGuidelineUrl ? (
          <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="p-3 bg-brand-teal/10 rounded-lg">
              <FileText className="h-6 w-6 text-brand-teal" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{data.brandGuidelineFile?.name}</p>
              <p className="text-xs text-muted-foreground">
                {data.brandGuidelineFile && (data.brandGuidelineFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={removeBrandGuideline}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <FileText className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Click to upload brand guidelines (PDF)
              </p>
            </div>
            <input 
              type="file" 
              className="hidden" 
              accept="application/pdf"
              onChange={handleBrandGuidelineUpload}
            />
          </label>
        )}
      </div>

      <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4">
        <p className="text-sm text-card-foreground">
          <strong>💡 Tip:</strong> We'll use your brand assets to customize the chat widget's 
          appearance, including colors, fonts, and logo placement.
        </p>
      </div>
    </div>
  );
};

export default StepBrandKit;
